// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

// ---------------------------------------------------------------------------
// Document extract — read the client's OWN documents into the inventory.
//
// The problem this solves: Optimum already has the answers. They are sitting in
// a vendor questionnaire response, an architecture document, a server
// inventory, a set of meeting notes. Today a consultant reads that document and
// retypes the values into fields, one at a time, for hundreds of applications.
//
// Why this is a different (and much safer) use of a model than free-form
// drafting: here the model is TRANSCRIBING from a document the user supplied,
// not recalling anything from training. That means every proposed value is
// checkable — it must come with the passage it came from, and this screen
// verifies that the quoted passage actually appears in the document before it
// will pre-check anything.
//
// THE RULES THIS SCREEN IS BUILT AROUND:
//   * /ai/extract is only ever READ from. Nothing is written until Apply.
//   * A proposal with NO quote is "unverified" — amber, and never pre-checked.
//   * A proposal whose quote cannot be found in the document is "not located" —
//     also amber and never pre-checked. A model can fabricate a quote; this is
//     the check that catches it.
//   * A proposal that would OVERWRITE an existing value starts unchecked and is
//     visually distinguished from a fill of an empty field.
//   * A value that is not legal for the field (picklists) cannot be accepted.
//   * Everything applied is stamped with extractedFrom / extractedAt /
//     extractedFields (+ the quote per field) so provenance stays auditable.
//   * Binary formats we cannot read (.pdf, .docx) are reported honestly and the
//     user is asked to paste the text. We never pretend to have parsed them.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, Table, TableHead, TableRow, TableCell,
  TableBody, Checkbox, Chip, TextField, Alert, AlertTitle, LinearProgress,
  CircularProgress, Tooltip, Divider, FormControlLabel, Autocomplete,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import * as XLSX from 'xlsx';
import { allFieldsFor, FIELD_TYPES } from './schema';
// Reused deliberately: the same "can this value legally live in this field?"
// gate the AI enrichment screen uses, so both screens reject the same things.
import { coerceSuggestion } from './AiEnrichment';
import { getConfig } from './config';
import { getIdToken } from './auth';
import store from './store';

const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const AMBER = '#B26A00';
const GREEN = '#2E7D32';
const RED = '#B0342C';
const HEAD = 'sectionHeader.main';
const headSx = { '& th': { fontWeight: 700, color: HEAD } };

// Long documents are sent in slices so a 200-page architecture doc does not
// blow the model's context; slices overlap so a value split across a boundary
// is still seen whole at least once.
const CHUNK_CHARS = 40000;
const CHUNK_OVERLAP = 800;
// Rendering guard for the preview pane only — the full text is always sent.
const MAX_PREVIEW_CHARS = 400000;

const TEXT_EXTS = ['txt', 'md', 'markdown', 'json', 'log', 'text'];
const SHEET_EXTS = ['csv', 'tsv'];
const BINARY_EXTS = ['pdf', 'docx', 'doc', 'pptx', 'xlsx', 'xls'];
const ACCEPT_ATTR = '.txt,.md,.csv,.json,.tsv,.log,.docx,.pdf';

const isBlank = (v) => v === undefined || v === null || v === '';

const displayValue = (v) => {
  if (isBlank(v)) return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
};

const truncate = (s, n) => {
  const str = String(s || '');
  return str.length > n ? `${str.slice(0, n)}…` : str;
};

const normalizeConfidence = (c) => {
  const v = String(c || '').toLowerCase();
  if (v.startsWith('h')) return 'high';
  if (v.startsWith('m')) return 'medium';
  if (v.startsWith('l')) return 'low';
  const n = Number(c);
  if (!Number.isNaN(n) && n > 0) {
    const scaled = n > 1 ? n / 100 : n;
    if (scaled >= 0.75) return 'high';
    if (scaled >= 0.5) return 'medium';
    return 'low';
  }
  return 'low'; // unknown confidence is treated as the weakest, never the strongest
};

const CONFIDENCE_STYLES = {
  high: { label: 'high', color: GREEN, bg: '#E8F5E9' },
  medium: { label: 'medium', color: AMBER, bg: '#FFF4E0' },
  low: { label: 'low', color: RED, bg: '#FDECEA' },
};
const CONFIDENCE_RANK = { high: 3, medium: 2, low: 1 };

// ---------------------------------------------------------------------------
// Schema surface — which fields a document may fill, grouped by section so the
// extraction can be scoped to the part of the fact sheet the document is about.
// ---------------------------------------------------------------------------
const EXTRACTABLE_FIELDS = allFieldsFor('Application').filter(
  (f) => f.type !== FIELD_TYPES.RELATION
    && f.type !== FIELD_TYPES.LIFECYCLE
    && f.type !== FIELD_TYPES.TAGS
    && f.key !== 'name'          // renaming the fact sheet is not an extraction
    && f.key !== 'catalogId'     // internal reference, never present in a document
);

const SECTIONS = [...new Set(EXTRACTABLE_FIELDS.map((f) => f.section))];

const FIELD_BY_KEY = (() => {
  const m = {};
  EXTRACTABLE_FIELDS.forEach((f) => { if (!m[f.key]) m[f.key] = f; });
  return m;
})();

const DEFAULT_SECTIONS = SECTIONS.filter(
  (s) => s === 'Information' || s === 'Classification & Ownership'
);

// ---------------------------------------------------------------------------
// Reading what the user gave us
// ---------------------------------------------------------------------------

// Cheap binary sniff so we never present mojibake as if it were the document.
function looksBinary(s) {
  if (!s) return false;
  const n = Math.min(s.length, 4000);
  let bad = 0;
  for (let i = 0; i < n; i++) {
    const c = s.charCodeAt(i);
    if (c === 0xFFFD || c < 9 || (c > 13 && c < 32)) bad += 1;
  }
  return bad / n > 0.02;
}

const PASTE_INSTEAD = 'Open it, select all, and paste the text into the box below — '
  + 'extraction works exactly the same on pasted text.';

async function readDocumentFile(file) {
  const ext = String(file.name.split('.').pop() || '').toLowerCase();

  if (SHEET_EXTS.includes(ext)) {
    // Delimited files go through xlsx (same reader the importer uses) so quoted
    // cells and odd delimiters come out as clean, readable text.
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const text = wb.SheetNames
      .map((n) => `--- ${n} ---\n${XLSX.utils.sheet_to_csv(wb.Sheets[n])}`)
      .join('\n\n');
    return { text };
  }

  if (BINARY_EXTS.includes(ext)) {
    return {
      text: '',
      error: `“${file.name}” is a ${ext.toUpperCase()} file. This screen reads plain text only — `
        + `it cannot open ${ext.toUpperCase()} in the browser, and it will not guess at the contents. ${PASTE_INSTEAD}`,
    };
  }

  const raw = await file.text();
  if (looksBinary(raw)) {
    return {
      text: '',
      error: `“${file.name}” does not decode as text — it looks like a binary or compressed format. ${PASTE_INSTEAD}`,
    };
  }
  if (!TEXT_EXTS.includes(ext)) {
    // Unknown extension that nonetheless decoded cleanly: use it, but say so.
    return { text: raw, note: `Read “${file.name}” as plain text — check the preview looks right.` };
  }
  return { text: raw };
}

// ---------------------------------------------------------------------------
// Locating a quote in the document. This is the verification step: a quote the
// model returns that does not actually appear in the text is not evidence.
// ---------------------------------------------------------------------------

// Whitespace-insensitive, case-insensitive index over the document, with a map
// back to real offsets so a match can be highlighted in the original text.
function buildSearchIndex(text) {
  let norm = '';
  const map = [];
  let lastWasSpace = false;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    const isSpace = c === 32 || c === 9 || c === 10 || c === 13 || c === 12 || c === 11 || c === 160;
    if (isSpace) {
      if (lastWasSpace) continue;
      norm += ' ';
      map.push(i);
      lastWasSpace = true;
    } else {
      norm += text[i].toLowerCase();
      map.push(i);
      lastWasSpace = false;
    }
  }
  return { norm, map };
}

const normalizeQuote = (q) => String(q || '').replace(/\s+/g, ' ').trim().toLowerCase();

function locateQuote(index, quote) {
  const needle = normalizeQuote(quote);
  if (!needle || !index || !index.norm) return null;
  let at = index.norm.indexOf(needle);
  if (at < 0 && needle.length > 40) {
    // Models often paraphrase the tail of a long quote; a solid leading run is
    // still enough to point the reviewer at the right passage.
    at = index.norm.indexOf(needle.slice(0, 40));
    if (at >= 0) {
      const start = index.map[at];
      const endIdx = Math.min(at + 39, index.map.length - 1);
      return { start, end: index.map[endIdx] + 1, partial: true };
    }
  }
  if (at < 0) return null;
  const start = index.map[at];
  const endIdx = Math.min(at + needle.length - 1, index.map.length - 1);
  return { start, end: index.map[endIdx] + 1, partial: false };
}

// ---------------------------------------------------------------------------
// Response shapes. The endpoint may not exist yet, so be generous about what a
// result looks like — but never invent a field the schema does not have.
// ---------------------------------------------------------------------------
function normalizeItem(key, raw) {
  const obj = (raw && typeof raw === 'object' && !Array.isArray(raw)) ? raw : { value: raw };
  return {
    key,
    value: obj.value,
    quote: obj.evidence || obj.quote || obj.excerpt || obj.sourceText || obj.passage || '',
    confidence: obj.confidence,
  };
}

function readResults(data) {
  if (!data || typeof data !== 'object') return [];
  const out = [];
  const pushMap = (map) => {
    if (!map || typeof map !== 'object') return;
    Object.keys(map).forEach((k) => out.push(normalizeItem(k, map[k])));
  };

  const list = Array.isArray(data.results) ? data.results
    : (Array.isArray(data.extractions) ? data.extractions : null);
  if (list) {
    list.forEach((item) => {
      if (!item || typeof item !== 'object') return;
      if (item.suggestions || item.fields) { pushMap(item.suggestions || item.fields); return; }
      const k = item.key || item.field || item.fieldKey;
      if (k) out.push(normalizeItem(k, item));
    });
    return out;
  }
  pushMap(data.fields || data.extracted || data.values);
  return out;
}

function chunkText(text) {
  if (text.length <= CHUNK_CHARS) return [text];
  const out = [];
  let i = 0;
  while (i < text.length) {
    out.push(text.slice(i, i + CHUNK_CHARS));
    i += CHUNK_CHARS - CHUNK_OVERLAP;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Small presentational pieces (module scope — they take everything as props).
// ---------------------------------------------------------------------------
function ConfidenceChip({ level }) {
  const s = CONFIDENCE_STYLES[level] || CONFIDENCE_STYLES.low;
  return (
    <Chip
      size="small"
      label={s.label}
      sx={{ height: 20, fontWeight: 700, color: s.color, bgcolor: s.bg, border: `1px solid ${s.color}33` }}
    />
  );
}

const STATUS_META = {
  verified: {
    label: 'quoted',
    color: GREEN,
    bg: '#E8F5E9',
    help: 'The quoted passage was found in the document — click it to see it in context.',
  },
  unlocated: {
    label: 'quote not found in document',
    color: AMBER,
    bg: '#FFF4E0',
    help: 'A quote was supplied but it does not appear in this document. Treat the value as unverified.',
  },
  missing: {
    label: 'unverified — no quote',
    color: AMBER,
    bg: '#FFF4E0',
    help: 'No supporting passage was returned, so there is nothing to check this value against.',
  },
};

// ---------------------------------------------------------------------------

export default function DocumentExtract({ tenantId, factSheetId }) {
  // ---- document -----------------------------------------------------------
  const [text, setText] = useState('');
  const [sourceName, setSourceName] = useState('');
  const [fileErr, setFileErr] = useState('');
  const [fileNote, setFileNote] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  // ---- target -------------------------------------------------------------
  const [apps, setApps] = useState([]);
  const [loadingApps, setLoadingApps] = useState(true);
  const [loadErr, setLoadErr] = useState('');
  const [targetId, setTargetId] = useState(factSheetId || '');
  const [sections, setSections] = useState(() => new Set(DEFAULT_SECTIONS));

  // ---- run ----------------------------------------------------------------
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [runErr, setRunErr] = useState('');
  const [unavailable, setUnavailable] = useState(false);

  // ---- review -------------------------------------------------------------
  const [rows, setRows] = useState([]);
  const [accepted, setAccepted] = useState(() => new Set());
  const [active, setActive] = useState(null);       // { start, end, rowKey }
  const [confirm, setConfirm] = useState(false);
  const [applying, setApplying] = useState(false);
  const [summary, setSummary] = useState(null);
  const markRef = useRef(null);

  // ---- data ---------------------------------------------------------------
  const reload = useCallback(() => {
    setLoadingApps(true);
    setLoadErr('');
    store.listFactSheets(tenantId, { type: 'Application' })
      .then((list) => setApps(list || []))
      .catch(() => setLoadErr('Could not load applications for this workspace.'))
      .finally(() => setLoadingApps(false));
  }, [tenantId]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { if (factSheetId) setTargetId(factSheetId); }, [factSheetId]);

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getIdToken()}`,
        ...(options.headers || {}),
      },
    });
    const body = await res.text();
    let data = {};
    try { data = body ? JSON.parse(body) : {}; } catch (e) { data = {}; }
    if (!res.ok) {
      const err = new Error(data.error || data.message || `request failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    return data;
  }, []);

  const targetApp = useMemo(
    () => apps.find((a) => a.id === targetId) || null,
    [apps, targetId]
  );

  const askedFields = useMemo(
    () => [...new Set(EXTRACTABLE_FIELDS.filter((f) => sections.has(f.section)).map((f) => f.key))],
    [sections]
  );

  const searchIndex = useMemo(() => buildSearchIndex(text), [text]);

  // ---- input handlers ------------------------------------------------------
  const acceptFile = useCallback(async (file) => {
    if (!file) return;
    setFileErr(''); setFileNote(''); setSummary(null);
    setRows([]); setAccepted(new Set()); setActive(null);
    try {
      const result = await readDocumentFile(file);
      if (result.error) {
        setFileErr(result.error);
        setText('');
        setSourceName('');
        return;
      }
      setText(result.text);
      setSourceName(file.name);
      if (result.note) setFileNote(result.note);
      if (!String(result.text).trim()) setFileErr(`“${file.name}” is empty — there is nothing to extract from.`);
    } catch (e) {
      setText('');
      setSourceName('');
      setFileErr(`Could not read “${file.name}”. ${PASTE_INSTEAD}`);
    }
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    acceptFile(file);
  };

  const onPickFile = (e) => {
    const file = e.target.files && e.target.files[0];
    acceptFile(file);
    e.target.value = ''; // allow re-picking the same file after a failed read
  };

  const onPasteChange = (e) => {
    setText(e.target.value);
    setFileErr(''); setFileNote('');
    if (!sourceName || sourceName.startsWith('pasted text')) {
      setSourceName(e.target.value.trim() ? 'pasted text' : '');
    }
    setRows([]); setAccepted(new Set()); setActive(null); setSummary(null);
  };

  const clearDocument = () => {
    setText(''); setSourceName(''); setFileErr(''); setFileNote('');
    setRows([]); setAccepted(new Set()); setActive(null); setSummary(null);
  };

  const toggleSection = (s) => {
    const next = new Set(sections);
    if (next.has(s)) next.delete(s); else next.add(s);
    setSections(next);
  };

  // ---- turning API results into review rows --------------------------------
  // The pre-check rule lives here and nowhere else: a value may only be
  // pre-checked when it is legal for the field, the field is currently empty,
  // AND its quote was found in the document.
  const buildRows = useCallback((items, app) => {
    const best = new Map();
    items.forEach((item) => {
      const field = FIELD_BY_KEY[item.key];
      if (!field) return;                      // never invent a field the schema lacks
      const located = locateQuote(searchIndex, item.quote);
      const status = !String(item.quote || '').trim()
        ? 'missing'
        : (located ? 'verified' : 'unlocated');
      const confidence = normalizeConfidence(item.confidence);
      const score = (status === 'verified' ? 10 : 0)
        + (item.quote ? 3 : 0)
        + CONFIDENCE_RANK[confidence];
      const prior = best.get(item.key);
      if (prior && prior.score >= score) return;
      best.set(item.key, { item, field, located, status, confidence, score });
    });

    const current = (app && app.fields) || {};
    const out = [];
    const preChecked = new Set();
    best.forEach((entry, key) => {
      const coerced = coerceSuggestion(entry.field, entry.item.value);
      const cur = current[key];
      const currentEmpty = isBlank(cur);
      if (coerced.ok && String(displayValue(coerced.value)) === String(displayValue(cur))) return; // no-op
      out.push({
        rowKey: key,
        fieldKey: key,
        fieldLabel: entry.field.label,
        section: entry.field.section,
        current: cur,
        currentEmpty,
        value: coerced.value,
        usable: coerced.ok,
        reason: coerced.reason,
        quote: String(entry.item.quote || ''),
        status: entry.status,
        match: entry.located,
        confidence: entry.confidence,
      });
      if (coerced.ok && currentEmpty && entry.status === 'verified') preChecked.add(key);
    });

    out.sort((a, b) => a.section.localeCompare(b.section) || a.fieldLabel.localeCompare(b.fieldLabel));
    setRows(out);
    setAccepted(preChecked);
    setActive(null);
    setSummary(null);
  }, [searchIndex]);

  // ---- the run -------------------------------------------------------------
  const runExtract = async () => {
    if (!text.trim() || !targetApp || !askedFields.length) return;
    setRunning(true);
    setRunErr('');
    setUnavailable(false);
    setProgress(0);
    setRows([]);
    setAccepted(new Set());
    setSummary(null);

    const chunks = chunkText(text);
    const collected = [];
    try {
      for (let i = 0; i < chunks.length; i++) {
        const payload = {
          text: chunks[i],
          factSheetId: targetApp.id,
          fields: askedFields,
          // Extra context the endpoint may use to transcribe more accurately;
          // ignoring it changes nothing.
          fieldDefs: askedFields.map((k) => ({
            key: k,
            label: FIELD_BY_KEY[k].label,
            type: FIELD_BY_KEY[k].type,
            picklist: FIELD_BY_KEY[k].picklist,
          })),
          applicationName: targetApp.name,
          source: sourceName,
          chunk: { index: i, total: chunks.length },
        };
        /* eslint-disable-next-line no-await-in-loop */
        const data = await api('/ai/extract', { method: 'POST', body: JSON.stringify(payload) });
        readResults(data).forEach((r) => collected.push(r));
        setProgress(Math.round(((i + 1) / chunks.length) * 100));
        /* eslint-disable-next-line no-await-in-loop */
        await new Promise((r) => setTimeout(r, 0)); // let the progress bar paint
      }
      buildRows(collected, targetApp);
      if (!collected.length) {
        setRunErr('The service found no values for these sections in this document.');
      }
    } catch (e) {
      setUnavailable(true);
      setRunErr((e && e.message) || 'The extraction service could not be reached.');
    } finally {
      setRunning(false);
    }
  };

  // ---- review state --------------------------------------------------------
  const usableRows = useMemo(() => rows.filter((r) => r.usable), [rows]);
  const acceptedRows = useMemo(
    () => usableRows.filter((r) => accepted.has(r.rowKey)),
    [usableRows, accepted]
  );
  const overwriteCount = acceptedRows.filter((r) => !r.currentEmpty).length;
  const unverifiedAccepted = acceptedRows.filter((r) => r.status !== 'verified').length;

  const grouped = useMemo(() => {
    const bySection = new Map();
    rows.forEach((r) => {
      const g = bySection.get(r.section);
      if (g) g.rows.push(r);
      else bySection.set(r.section, { section: r.section, rows: [r] });
    });
    return [...bySection.values()];
  }, [rows]);

  const toggleRow = (rowKey) => {
    const next = new Set(accepted);
    if (next.has(rowKey)) next.delete(rowKey); else next.add(rowKey);
    setAccepted(next);
    setSummary(null);
  };
  const checkVerifiedFills = () => {
    const next = new Set(accepted);
    usableRows.forEach((r) => { if (r.currentEmpty && r.status === 'verified') next.add(r.rowKey); });
    setAccepted(next);
    setSummary(null);
  };
  const clearAll = () => { setAccepted(new Set()); setSummary(null); };

  const showEvidence = (row) => {
    if (!row.match) return;
    setActive({ start: row.match.start, end: row.match.end, rowKey: row.rowKey });
  };

  useEffect(() => {
    if (active && markRef.current && markRef.current.scrollIntoView) {
      markRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [active]);

  // ---- apply ---------------------------------------------------------------
  const applyAccepted = async () => {
    if (!acceptedRows.length || !targetId) return;
    setConfirm(false);
    setApplying(true);
    setSummary(null);
    try {
      const fs = await store.getFactSheet(targetId);
      if (!fs) throw new Error('the target application could not be loaded');
      const existing = fs.fields || {};
      const stamp = new Date().toISOString();
      const patch = {};
      const evidence = { ...(existing.extractedEvidence || {}) };
      acceptedRows.forEach((r) => {
        patch[r.fieldKey] = r.value;
        evidence[r.fieldKey] = {
          quote: truncate(r.quote, 600),
          verified: r.status === 'verified',
          confidence: r.confidence,
          source: sourceName || 'supplied document',
          at: stamp,
        };
      });
      const priorKeys = Array.isArray(existing.extractedFields) ? existing.extractedFields : [];
      await store.saveFactSheet({
        ...fs,
        fields: {
          ...existing,                 // merge — everything not accepted is preserved
          ...patch,
          extractedFrom: sourceName || 'supplied document',
          extractedAt: stamp,
          extractedFields: [...new Set([...priorKeys, ...acceptedRows.map((r) => r.fieldKey)])],
          extractedEvidence: evidence, // the passage behind every value, kept for audit
        },
      });
      setSummary({
        ok: true,
        count: acceptedRows.length,
        appName: targetApp ? targetApp.name : targetId,
        overwrites: overwriteCount,
      });
      setRows([]);
      setAccepted(new Set());
      setActive(null);
      reload();
    } catch (e) {
      setSummary({ ok: false, error: (e && e.message) || 'save failed' });
    } finally {
      setApplying(false);
    }
  };

  // ---- preview -------------------------------------------------------------
  const previewParts = useMemo(() => {
    const t = text.length > MAX_PREVIEW_CHARS ? text.slice(0, MAX_PREVIEW_CHARS) : text;
    if (!active || active.start >= t.length) return { before: t, hit: '', after: '' };
    const end = Math.min(active.end, t.length);
    return { before: t.slice(0, active.start), hit: t.slice(active.start, end), after: t.slice(end) };
  }, [text, active]);

  const busy = running || applying;
  const chunkCount = useMemo(() => (text ? chunkText(text).length : 0), [text]);
  const hasDocument = !!text.trim();

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
        <DescriptionOutlinedIcon sx={{ color: BLUE }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY }}>Extract from a document</Typography>
        <Chip
          size="small" label="EVIDENCE REQUIRED — REVIEW BEFORE APPLYING"
          sx={{ height: 20, fontWeight: 700, bgcolor: '#EEF4FB', color: BLUE, border: `1px solid ${BLUE}44` }}
        />
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Reload applications">
          <span>
            <Button size="small" startIcon={<RefreshIcon />} onClick={reload} disabled={busy}>Reload</Button>
          </span>
        </Tooltip>
      </Stack>

      <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ mb: 2 }}>
        This screen <b>transcribes values out of a document you supply</b> — a vendor questionnaire
        response, an architecture document, a server inventory, meeting notes. It is not answering
        from general knowledge, so every proposed value must arrive with the passage it came from,
        and that passage is checked against the document before anything is pre-checked. Nothing is
        saved until you press <b>Apply</b>.
      </Alert>

      {loadErr && <Alert severity="error" sx={{ mb: 2 }}>{loadErr}</Alert>}

      {/* ------------------------------ 1. the document --------------------- */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700, mb: 1.5 }}>
          1 · Provide the document
        </Typography>

        {fileErr && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setFileErr('')}>{fileErr}</Alert>}
        {fileNote && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setFileNote('')}>{fileNote}</Alert>}

        <Box
          onClick={() => fileRef.current && fileRef.current.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          sx={{
            border: `2px dashed ${dragOver ? BLUE : '#cfd8dc'}`,
            borderRadius: 2, p: 3, textAlign: 'center', cursor: 'pointer',
            bgcolor: dragOver ? '#f7fbff' : undefined,
            '&:hover': { borderColor: BLUE, bgcolor: '#f7fbff' },
          }}
        >
          <UploadFileIcon sx={{ fontSize: 40, color: BLUE }} />
          <Typography sx={{ fontWeight: 600, mt: 1 }}>Drop a document here, or click to choose one</Typography>
          <Typography variant="body2" sx={{ color: GREY }}>
            .txt, .md, .csv, .json read directly. <b>.pdf and .docx cannot be read in the browser</b> —
            drop one and you will be told to paste the text instead, not handed a guess.
          </Typography>
          <input ref={fileRef} type="file" accept={ACCEPT_ATTR} hidden onChange={onPickFile} />
        </Box>

        <Divider sx={{ my: 2 }}><Typography variant="caption" sx={{ color: GREY }}>or paste the text</Typography></Divider>

        <TextField
          multiline minRows={4} maxRows={12} fullWidth size="small"
          placeholder="Paste the questionnaire response, architecture notes, inventory rows, or meeting notes here…"
          value={text} onChange={onPasteChange} disabled={busy}
        />

        {hasDocument && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
            <Chip size="small" label={sourceName || 'pasted text'} onDelete={busy ? undefined : clearDocument} />
            <Chip size="small" variant="outlined" label={`${text.length.toLocaleString()} characters`} />
            {chunkCount > 1 && (
              <Chip
                size="small" variant="outlined"
                label={`sent in ${chunkCount} overlapping parts`}
                sx={{ color: GREY }}
              />
            )}
          </Stack>
        )}
      </Paper>

      {/* ------------------------------ 2. the target ----------------------- */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700, mb: 1.5 }}>
          2 · Which application is this document about?
        </Typography>

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 340, flex: 1 }}
            options={apps}
            loading={loadingApps}
            disabled={busy}
            value={targetApp}
            onChange={(e, v) => { setTargetId(v ? v.id : ''); setRows([]); setAccepted(new Set()); setSummary(null); }}
            getOptionLabel={(o) => (o && o.name) || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderOption={(props, o) => (
              <li {...props} key={o.id}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{o.name}</Typography>
                  <Typography variant="caption" sx={{ color: GREY }}>
                    {(o.fields || {}).vendorName || 'no vendor recorded'}
                    {' · '}
                    {(o.fields || {}).hostingType || 'hosting unknown'}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Target application"
                placeholder="Search applications…"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingApps ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {!!factSheetId && (
            <Typography variant="caption" sx={{ color: GREY }}>
              opened from a fact sheet — change it here if the document is about a different application
            </Typography>
          )}
        </Stack>

        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          Which sections should be extracted?
        </Typography>
        <Typography variant="caption" sx={{ color: GREY, display: 'block', mb: 1 }}>
          Scoping the request to the part of the fact sheet the document actually covers keeps the
          model on the passages that matter and cuts down on speculative answers.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {SECTIONS.map((s) => (
            <FormControlLabel
              key={s}
              sx={{ m: 0, mr: 1.5 }}
              control={(
                <Checkbox
                  size="small" checked={sections.has(s)}
                  onChange={() => toggleSection(s)} disabled={busy}
                />
              )}
              label={<span style={{ fontSize: 13 }}>{s}</span>}
            />
          ))}
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
          <Button
            variant="contained" startIcon={<FormatQuoteIcon />}
            disabled={busy || !hasDocument || !targetApp || !askedFields.length}
            onClick={runExtract}
            sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
          >
            {running ? `Reading ${progress}%` : 'Extract values from this document'}
          </Button>
          <Typography variant="caption" sx={{ color: GREY }}>
            {askedFields.length} field{askedFields.length === 1 ? '' : 's'} in scope · nothing is written by this step
          </Typography>
        </Stack>
        {running && <LinearProgress variant="determinate" value={progress} sx={{ mt: 1.5 }} />}
      </Paper>

      {unavailable && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Document extraction is not enabled for this environment</AlertTitle>
          The <code>/ai/extract</code> service did not respond{runErr ? ` (${runErr})` : ''}. Nothing was
          changed and nothing was lost — the document is still loaded below, and you can read it here
          while the values are entered by hand.
        </Alert>
      )}
      {!unavailable && runErr && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setRunErr('')}>{runErr}</Alert>
      )}

      {summary && (
        <Alert
          severity={summary.ok ? 'success' : 'error'} sx={{ mb: 2 }}
          onClose={() => setSummary(null)}
        >
          {summary.ok ? (
            <>
              <AlertTitle>Applied</AlertTitle>
              Wrote {summary.count} value{summary.count === 1 ? '' : 's'} to <b>{summary.appName}</b>
              {summary.overwrites ? ` · ${summary.overwrites} replaced an existing value` : ''}. Each one is
              stamped with the document it came from and the passage behind it.
            </>
          ) : (
            <>
              <AlertTitle>Nothing was saved</AlertTitle>
              {summary.error}
            </>
          )}
        </Alert>
      )}

      {/* ------------------- 3. review + document, side by side -------------- */}
      {(hasDocument || !!rows.length) && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.35fr) minmax(0, 1fr)' },
            gap: 2, alignItems: 'start',
          }}
        >
          {/* -------- proposals -------- */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
              <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700 }}>
                3 · Review each value against the document
              </Typography>
              {!!rows.length && (
                <>
                  <Chip size="small" label={`${rows.length} proposed`} sx={{ fontWeight: 600 }} />
                  <Chip
                    size="small" label={`${acceptedRows.length} checked`}
                    sx={{ fontWeight: 600, bgcolor: acceptedRows.length ? '#e3f0fa' : undefined }}
                  />
                  {overwriteCount > 0 && (
                    <Chip
                      size="small" label={`${overwriteCount} would overwrite`}
                      sx={{ fontWeight: 700, bgcolor: '#FDECEA', color: RED }}
                    />
                  )}
                </>
              )}
            </Stack>

            {!rows.length ? (
              <Typography variant="body2" sx={{ color: GREY }}>
                No proposals yet. Choose the target application and the sections above, then run the
                extraction — the document stays on screen so you can read it either way.
              </Typography>
            ) : (
              <>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <Button size="small" onClick={checkVerifiedFills} disabled={applying}>
                    Check all quoted fills of empty fields
                  </Button>
                  <Button size="small" onClick={clearAll} disabled={applying}>Clear all</Button>
                </Stack>

                <Paper variant="outlined" sx={{ maxHeight: 560, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={headSx}>
                        <TableCell>Field</TableCell>
                        <TableCell>Current value</TableCell>
                        <TableCell>Proposed value</TableCell>
                        <TableCell>Quoted evidence from the document</TableCell>
                        <TableCell>Confidence</TableCell>
                        <TableCell align="center">Accept?</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {grouped.map((g) => (
                        <React.Fragment key={g.section}>
                          <TableRow sx={{ bgcolor: '#F4F7FA' }}>
                            <TableCell colSpan={6} sx={{ fontWeight: 700, color: NAVY }}>
                              {g.section}
                              <Typography component="span" variant="caption" sx={{ color: GREY, ml: 1 }}>
                                {g.rows.length} value{g.rows.length === 1 ? '' : 's'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          {g.rows.map((r) => {
                            const meta = STATUS_META[r.status];
                            const isActive = !!active && active.rowKey === r.rowKey;
                            return (
                              <TableRow
                                key={r.rowKey} hover selected={accepted.has(r.rowKey)}
                                sx={!r.currentEmpty
                                  ? { bgcolor: '#FFFBF2', '& td:first-of-type': { borderLeft: `3px solid ${AMBER}` } }
                                  : undefined}
                              >
                                <TableCell sx={{ color: GREY, whiteSpace: 'nowrap' }}>{r.fieldLabel}</TableCell>
                                <TableCell sx={{ maxWidth: 180, color: r.currentEmpty ? '#C77700' : NAVY }}>
                                  {r.currentEmpty
                                    ? <em>empty</em>
                                    : (
                                      <Tooltip title={displayValue(r.current)}>
                                        <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {displayValue(r.current)}
                                        </Box>
                                      </Tooltip>
                                    )}
                                </TableCell>
                                <TableCell sx={{ maxWidth: 220, fontWeight: 600 }}>
                                  {displayValue(r.value)}
                                  {!r.usable && (
                                    <Chip
                                      size="small" label={`rejected — ${r.reason}`}
                                      sx={{ ml: 0.5, height: 18, fontWeight: 700, bgcolor: '#FDECEA', color: RED }}
                                    />
                                  )}
                                  {r.usable && !r.currentEmpty && (
                                    <Chip
                                      size="small" label="overwrites"
                                      sx={{ ml: 0.5, height: 18, fontWeight: 700, bgcolor: '#FFF4E0', color: AMBER }}
                                    />
                                  )}
                                </TableCell>
                                <TableCell sx={{ maxWidth: 320 }}>
                                  {r.status === 'verified' ? (
                                    <Tooltip title={meta.help}>
                                      <Box
                                        component="span"
                                        onClick={() => showEvidence(r)}
                                        sx={{
                                          display: 'block', cursor: 'pointer', fontStyle: 'italic',
                                          color: isActive ? NAVY : GREY,
                                          bgcolor: isActive ? '#FFF6CC' : undefined,
                                          borderLeft: `3px solid ${GREEN}`, pl: 1, py: 0.25,
                                          '&:hover': { bgcolor: '#FFF6CC' },
                                        }}
                                      >
                                        “{truncate(r.quote, 180)}”
                                        {r.match && r.match.partial && (
                                          <Typography variant="caption" sx={{ color: AMBER, display: 'block' }}>
                                            only the opening of this quote matched the document
                                          </Typography>
                                        )}
                                      </Box>
                                    </Tooltip>
                                  ) : (
                                    <Box>
                                      <Chip
                                        size="small" label={meta.label}
                                        sx={{ height: 18, fontWeight: 700, color: meta.color, bgcolor: meta.bg, border: `1px solid ${meta.color}` }}
                                      />
                                      {r.status === 'unlocated' && (
                                        <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                                          “{truncate(r.quote, 120)}”
                                        </Typography>
                                      )}
                                      <Typography variant="caption" sx={{ color: AMBER, display: 'block', mt: 0.5 }}>
                                        {r.status === 'missing'
                                          ? 'Left unchecked: nothing in the document was cited for this value, so there is no way to confirm it.'
                                          : 'Left unchecked: the cited passage is not in this document, so the value cannot be confirmed.'}
                                      </Typography>
                                    </Box>
                                  )}
                                </TableCell>
                                <TableCell><ConfidenceChip level={r.confidence} /></TableCell>
                                <TableCell align="center" padding="checkbox">
                                  <Tooltip title={r.usable
                                    ? (r.currentEmpty
                                      ? 'Fills an empty field'
                                      : 'This would OVERWRITE an existing value — check it deliberately')
                                    : `Cannot be accepted: proposed value is ${r.reason}`}>
                                    <span>
                                      <Checkbox
                                        size="small" checked={accepted.has(r.rowKey)}
                                        disabled={applying || !r.usable}
                                        onChange={() => toggleRow(r.rowKey)}
                                      />
                                    </span>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>

                <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 1.5 }}>
                  Click a quote to jump to it in the document. Values with no quote, or whose quote is
                  not in the document, stay unchecked — they are the ones most likely to be invented.
                  Values that would replace something already recorded also stay unchecked.
                </Typography>

                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
                  <Button
                    variant="contained" disabled={applying || !acceptedRows.length}
                    onClick={() => setConfirm(true)}
                    sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
                  >
                    {applying ? 'Saving…' : `Apply ${acceptedRows.length} value${acceptedRows.length === 1 ? '' : 's'}`}
                  </Button>
                  <Typography variant="caption" sx={{ color: GREY }}>
                    to {targetApp ? targetApp.name : 'the selected application'}
                    {overwriteCount ? ` · ${overwriteCount} overwrite existing values` : ''}
                  </Typography>
                </Stack>
                {applying && <LinearProgress sx={{ mt: 1.5 }} />}
              </>
            )}
          </Paper>

          {/* -------- document preview -------- */}
          <Paper variant="outlined" sx={{ p: 2, position: { lg: 'sticky' }, top: 16 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
              <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700 }}>
                Document
              </Typography>
              <Chip size="small" variant="outlined" label={sourceName || 'pasted text'} />
              {active && (
                <Button size="small" onClick={() => setActive(null)}>Clear highlight</Button>
              )}
            </Stack>
            {text.length > MAX_PREVIEW_CHARS && (
              <Typography variant="caption" sx={{ color: AMBER, display: 'block', mb: 1 }}>
                Showing the first {MAX_PREVIEW_CHARS.toLocaleString()} characters — the whole document
                is still sent for extraction.
              </Typography>
            )}
            <Box
              sx={{
                maxHeight: 620, overflow: 'auto', p: 1.5, bgcolor: '#FAFBFC',
                border: '1px solid #E3E8EE', borderRadius: 1,
              }}
            >
              <Box
                component="pre"
                sx={{
                  m: 0, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: 12.5, lineHeight: 1.6, whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word', color: NAVY,
                }}
              >
                {previewParts.before}
                {!!previewParts.hit && (
                  <Box
                    component="mark"
                    ref={markRef}
                    sx={{ bgcolor: '#FFF0A8', color: NAVY, fontWeight: 700, borderRadius: 0.5, px: 0.25 }}
                  >
                    {previewParts.hit}
                  </Box>
                )}
                {previewParts.after}
              </Box>
            </Box>
          </Paper>
        </Box>
      )}

      <Dialog open={confirm} onClose={() => !applying && setConfirm(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>Apply extracted values?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This will write <b>{acceptedRows.length}</b> value{acceptedRows.length === 1 ? '' : 's'} to{' '}
            <b>{targetApp ? targetApp.name : 'the selected application'}</b>. Every other field on that
            fact sheet is left exactly as it is.
          </Typography>
          {overwriteCount > 0 && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              <b>{overwriteCount}</b> of these replace a value that is already recorded.
            </Alert>
          )}
          {unverifiedAccepted > 0 && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              <b>{unverifiedAccepted}</b> of these have <b>no verified quote</b> in the document. You
              checked them by hand — they will be saved and marked as unverified.
            </Alert>
          )}
          <Typography variant="caption" sx={{ color: GREY }}>
            The fact sheet is stamped <code>extractedFrom</code>, <code>extractedAt</code> and{' '}
            <code>extractedFields</code>, and the supporting passage is stored per field, so anyone can
            later see exactly which document each value came from.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)} disabled={applying}>Cancel</Button>
          <Button
            variant="contained" onClick={applyAccepted} disabled={applying}
            sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
          >
            Apply and save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
